import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUserRecognitions } from '@/hooks/useUserRecognitions';
import { useMostRecognizedTechniques } from '@/hooks/useMostRecognizedTechniques';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import GiftIcon from '@/assets/icons/gift.svg';
import { useI18n } from '@/contexts/I18nContext';

interface Recognition {
  id: string;
  praiser: {
    id: string;
    full_name: string;
    username: string;
    avatar_url?: string;
    belt_rank?: string;
  };
  feedback_text: string;
  created_at: string;
  techniques: Array<{
    id: number;
    name: string;
  }>;
}

interface RecognizedTechnique {
  id: number;
  name: string;
  recognition_count: number;
}

export const UserRecognitions: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<'recognitions' | 'techniques'>('recognitions');
  
  const { data: recognitions, isLoading: recognitionsLoading, error: recognitionsError } = useUserRecognitions(userId || '');
  const { data: mostRecognizedTechniques, isLoading: techniquesLoading, error: techniquesError } = useMostRecognizedTechniques(userId || '');

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (recognitionsLoading || techniquesLoading) {
    return (
      <div className="min-h-screen bg-[#160C0C] text-white">
        <TopNavigation variant="subtle" />
        <div className="pt-16">
          {/* Loading skeleton */}
          <div className="p-4">
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Most Recognized Techniques</h2>
                <button 
                  onClick={() => setActiveTab('techniques')}
                  className="text-[#D72E16] text-sm font-medium hover:text-[#B8251A] transition-colors cursor-pointer"
                >
                  {t('calendar.see_all')}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-[#281B1B] rounded-full px-3 py-1 animate-pulse">
                    <div className="w-20 h-6 bg-[#3A2A2A] rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (recognitionsError || techniquesError) {
    return (
      <div className="min-h-screen bg-[#160C0C] text-white">
        <TopNavigation variant="subtle" />
        <div className="pt-16">
          <div className="flex items-center justify-between p-4 border-b border-[#281B1B]">
            <button onClick={handleBack} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">{t('recognitions.back')}</span>
            </button>
            <h1 className="text-lg font-semibold">{t('profile.recognitions')}</h1>
            <div className="w-20"></div>
          </div>
          <div className="p-4">
            <p className="text-white/60">{t('recognitions.error_loading_recognitions')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#160C0C] text-white">
      <TopNavigation variant="subtle" />
      
      {/* Header */}
      <div className="pt-16">
        <div className="flex items-center justify-between p-4 border-b border-[#281B1B]">
          <button onClick={handleBack} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">{t('recognitions.back')}</span>
          </button>
          <h1 className="text-lg font-semibold">{t('profile.recognitions')}</h1>
          <div className="w-20"></div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Most Recognized Techniques Section - Only show when there are techniques */}
          {mostRecognizedTechniques && mostRecognizedTechniques.length > 0 && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Most Recognized Techniques</h2>
                <button 
                  onClick={() => setActiveTab('techniques')}
                  className="text-[#D72E16] text-sm font-medium hover:text-[#B8251A] transition-colors cursor-pointer"
                >
                  {t('calendar.see_all')}
                </button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {mostRecognizedTechniques.slice(0, 5).map((technique) => (
                  <div key={technique.id} className="bg-[#D72E16] text-white rounded-full px-3 py-1 flex items-center gap-2">
                    <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                      <span className="text-black text-[10px] font-bold">{technique.recognition_count}</span>
                    </div>
                    <span className="text-sm font-medium">{technique.name.replace(/\(e\.g.*?\)/gi, '').trim()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabs - Only show when there are recognitions or techniques */}
          {(recognitions && recognitions.length > 0) || (mostRecognizedTechniques && mostRecognizedTechniques.length > 0) ? (
            <>
              <div className="flex gap-0 mb-4 border-b border-[#281B1B]">
                <button
                  onClick={() => setActiveTab('recognitions')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'recognitions'
                      ? 'bg-[#D72E16] text-white'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  {t('recognitions.recognitions_tab')}
                </button>
                <button 
                  onClick={() => setActiveTab('techniques')} 
                  className={`flex-1 pb-2 px-1 text-sm font-semibold ${
                    activeTab === 'techniques' 
                      ? 'text-white border-b-2 border-[#D72E16]' 
                      : 'text-white/60 border-b-2 border-transparent'
                  }`}
                >
                  Techniques
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'recognitions' && (
                <div className="space-y-4">
                  {recognitions && recognitions.length > 0 ? (
                    recognitions.map((recognition) => (
                      <div key={recognition.id} className="bg-[#281B1B] rounded-2xl p-4 border border-[#3A2A2A]">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1">
                            {/* Framed Avatar */}
                            <div className="flex-shrink-0">
                              <FramedAvatar
                                src={recognition.praiser.avatar_url}
                                alt={recognition.praiser.full_name}
                                initials={recognition.praiser.full_name.charAt(0)}
                                size={40}
                                frameSize={{ w: 50, h: 55 }}
                                beltRank={recognition.praiser.belt_rank || 'white'}
                                showFrame={true}
                              />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <span className="text-white font-semibold text-sm block">
                                {recognition.praiser.full_name}
                              </span>
                              <span className="text-white/60 text-xs block mb-2">
                                @{recognition.praiser.username}
                              </span>
                              <p className="text-white/80 text-sm">
                                {recognition.feedback_text}
                              </p>
                            </div>
                          </div>
                          
                          {/* Date */}
                          <span className="text-white/60 text-xs flex-shrink-0">
                            {formatDate(recognition.created_at)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-[#281B1B] rounded-full flex items-center justify-center mb-4">
                        <span className="text-[#D72E16] text-2xl">🏆</span>
                      </div>
                      <h3 className="text-white font-semibold text-lg mb-2">{t('recognitions.no_recognitions_yet')}</h3>
                      <p className="text-white/60 text-sm max-w-xs">
                        {t('recognitions.empty_state_subtitle')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'techniques' && (
                <div className="space-y-4">
                  {mostRecognizedTechniques && mostRecognizedTechniques.length > 0 ?
                    mostRecognizedTechniques.map((technique) => (
                      <div key={technique.id} className="bg-[#281B1B] rounded-2xl p-4 border border-[#3A2A2A]">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#D72E16] rounded-full flex items-center justify-center">
                              <span className="text-white text-sm font-bold">
                                {technique.name.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <h3 className="text-white font-semibold text-sm">{technique.name.replace(/\(e\.g.*?\)/gi, '').trim()}</h3>
                              <p className="text-white/60 text-xs">
                                {technique.recognition_count} recognition{technique.recognition_count !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="bg-[#D72E16] text-white text-sm font-bold px-3 py-1 rounded-full">
                            {technique.recognition_count}
                          </div>
                        </div>
                      </div>
                    ))
                  : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 bg-[#281B1B] rounded-full flex items-center justify-center mb-4">
                        <span className="text-[#D72E16] text-2xl">🥋</span>
                      </div>
                      <h3 className="text-white font-semibold text-lg mb-2">{t('recognitions.no_techniques_recognized_yet')}</h3>
                      <p className="text-white/60 text-sm max-w-xs">
                        {t('recognitions.techniques_empty_state_subtitle')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* Empty State - Show when there are no recognitions and no techniques */
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
              {/* Empty State Icon */}
              <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                <img 
                  src={GiftIcon} 
                  alt="Recognitions" 
                  className="w-10 h-10 text-white/40"
                  style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
                />
              </div>
              
              {/* Empty State Title */}
              <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                {t('recognitions.no_recognitions_yet')}
              </h3>
              
              {/* Empty State Subtitle */}
              <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                {t('recognitions.empty_state_subtitle')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 